import { Box } from "@mui/material";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { OrgUtils } from "@smartrr/shared/entities/Organization";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { updateDateRecur } from "@smartrr/shared/utils/dateUtils";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useDisabledDatesByBillingDays } from "@customer-app/account/views/OperationAquarius/hooks/useDisabledDatesByBillingDays";
import { useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import { orderNow } from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

import { getProcessingDateCopy } from "./libs/utils";
import { CenterText } from "../CenterText";

interface IOrderNowProps {
  setIconButtonInactive: () => void;
  purchaseState: IPurchaseState;
}

export const OrderNowView = ({ setIconButtonInactive, purchaseState }: IOrderNowProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [updatedNextBillingDate, setUpdatedNextBillingDate] = useState<Date>();

  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const organization = useOrganization();

  const disabledDatesFromBilling = useDisabledDatesByBillingDays(purchaseState.nextBillingDate);
  const boxRef = useRef<HTMLDivElement>(null);

  const nextVendorBillingDateTime = OrgUtils.getNextVendorBillingDateTime(organization);

  const nextBillingDate = new Date(nextVendorBillingDateTime);

  useEffect(() => {
    if (!updatedNextBillingDate) {
      // if the shop's next billing date is within the array of invalid dates, set date to updatedNextBillingDate and update
      // updatedNextBillingDate until it's no longer invalid

      setUpdatedNextBillingDate(updateDateRecur(disabledDatesFromBilling, nextBillingDate));
    }
  }, [updatedNextBillingDate]);

  const orderNowHandler = useCallback(async () => {
    setIsLoading(true);
    await dispatch(orderNow(purchaseState.id, updatedNextBillingDate)).then(async res => {
      if (res.type === "ORDER_NOW_SUCCESS") {
        toast(typedLocalize(`subscriptions.yourSubscriptions.orderOptionsModal.sendNowView.sendNowToast`));
        closeModal();
      } else {
        toast.error(typedLocalize("errorMessages.body"));
      }
    });
    setIsLoading(false);
  }, [purchaseState, updatedNextBillingDate]);

  return (
    <Box ref={boxRef}>
      <CenterText title={typedLocalize(`subscriptions.yourSubscriptions.orderOptionsModal.sendNowView.sendNow`)}>
        {getProcessingDateCopy(nextBillingDate, updatedNextBillingDate!)}
      </CenterText>
      <ConfirmCancelButtons onCancel={setIconButtonInactive} onConfirm={orderNowHandler} isLoading={isLoading} />
    </Box>
  );
};
